import * as React from "react";
import { Slider } from "@bizform/primereact/components/slider/Slider";
import { SliderProps } from "@bizform/primereact/slider";
import { InputText } from "@bizform/primereact/components/inputtext/InputText";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { WindowKey } from "@bizform/type/model/model/WindowKey.model";
import { ExceptionType } from "@bizform/type/model/model/Exception.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit, I18N } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = SliderProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    value?: number|[number, number];
    min?: number;
    max?: number;
    orientation?: string;
    step?: number;
    range?: boolean;
    style?: object;
    rangeClassName?: string;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    disabled?: boolean;
    tabIndex?: string;
    // event
    onChange?(e: {originalEvent: Event, value: number|[number, number]}): void;
    onSlideEnd?(e: {originalEvent: Event, value: number|[number, number]}): void;
};

type WrappedFormState = {
    onChange?: string;
    onSlideEnd?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi
 * @class BfSlider
 * @description BfSlider component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<number|[number, number]>>()
export class BfSlider extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_TX_AItRlS6BIwngLlllPMPpLpxqPJdn",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.slider.label,
        description: I18n.componentElement.slider.description,
        icon: "",
        dataType: [DataType.INTEGER],
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._slider.focus();
        } if (this._slider) {
            (this._slider.querySelector("span[tabindex='0']") as HTMLInputElement).focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);

            if (ComponentHelper.isNull(newState.wrappedCompState.min)) {
                newState.wrappedCompState.min = 0;
            }

            if (ComponentHelper.isNull(newState.wrappedCompState.max)) {
                newState.wrappedCompState.max = 100;
            }

            if (newState.wrappedCompState.max < newState.wrappedCompState.min) {
                newState.wrappedCompState.max = newState.wrappedCompState.min;
            }

            newState.wrappedCompState.value = Math.max(newState.wrappedCompState.min, Math.min(newState.wrappedCompState.max, newState.wrappedCompState.value as number));

            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: number|[number, number]): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: number|[number, number]): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: this.accessibility ? "" as any : 0
                }
            };
            this.setState(newState);
        } else {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfSlider)
    static init(): ComponentElement<number|[number, number]> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _slider: any;
    private _parentClass: string;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: this.accessibility ? null : 0,
                min: 0,
                max: 100
            },
            wrappedFormState: {}
        };
        this._parentClass = "w-100";

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._onSlideEndEvent = this._onSlideEndEvent.bind(this);
        this._focusHandler = this._focusHandler.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<SliderProps & {}>, prevState: Readonly<BaseStateModel & { wrappedCompState: WrappedCompState }>, snapshot?: any): void {
        if (this._slider) {
            this._makeStyle();
        }
        if (!this.accessibility) {
            if (this.state.wrappedCompState.rangeClassName) {
                if (this._slider) {
                    this._slider.getElementsByTagName("span")[0].setAttribute("class", `p-slider-range p-slider-range-min ui-widget-header ui-corner-all ${this.state.wrappedCompState.rangeClassName}`);
                }
            } else {
                if (this._slider) {
                    this._slider.getElementsByTagName("span")[0].setAttribute("class", "p-slider-range p-slider-range-min ui-widget-header ui-corner-all");
                }
            }
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._slider = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            if (this.accessibility) {
                const v: number = Number(e.target.value);
                if ((e.target.value !== "-" || (e.target.value === "-" && this.state.wrappedCompState.min > -1)) && (isNaN(v) || v < this.state.wrappedCompState.min || v > this.state.wrappedCompState.max)) {
                    window[WindowKey.CORELIBRARY].ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.slider.invalidValue}"));
                    e.value = e.target.value = "";
                } else {
                    e.value = e.target.value;
                }
            }
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: e.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _onSlideEndEvent(event): void {
    }

    private _focusHandler(e?) {
        e.stopPropagation();
        e.preventDefault();
        const {min, max} = this.state.wrappedCompState;
        let value = this.state.wrappedCompState.value;
        if (value < min || value > max || typeof value !== "number" || Number(value) % 1 !== 0) {
            if (typeof value !== "number" || Number(value) % 1 !== 0) {
                value =  null;
            } else {
                value = value < min ? min : value > max ? max : value;
            }
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            const aria: any = {
                "aria-label": I18N.translate("{$I._app.accessibility.component.slider.value}", {min: this.state.wrappedCompState.min, max: this.state.wrappedCompState.max})
            };
            if (newProps.tabindex || newProps.tabindex === 0) {
                aria.tabindex = newProps.tabindex;
            }
            return (
                <div className="w-100">
                    <InputText
                        {...aria}
                        key={this.elementId}
                        id={this.elementId}
                        value={String(newProps.value)}
                        onChange={newProps.onChange as any}
                        ref={this._setRef}
                    />
                </div>
            );
        } else {
            newProps.tabIndex = String(newProps.tabindex);
            return (
                <div className={this._parentClass} onClick={this._focusHandler}>
                    <label className={"p-component p-filled"} style={{textAlign: "center", borderRadius: "0px", width: "calc(100% - 4px)", margin: "0 2px"}}>{newProps.value}</label>
                    <Slider
                        {...newProps}
                        key={this.elementId}
                        id={this.elementId}
                        range={false}
                        ref={this._setRef}
                        style={{width: "calc(100% - 4px)", margin: "0 2px"}}
                        className={null}
                    />
                </div>
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readonly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        const { value } = this.state.wrappedCompState;

        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._slider = this.accessibility ? ref.element : ref.el;
            this._makeStyle();
            this.readyComponent();
        }
    }

    private _makeStyle(newProps = {...this.state.wrappedCompState}) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (!this.accessibility && this._slider && this._slider.parentNode) {
                let parentNode = this._slider.parentNode;
                parentNode.setAttribute("style", "");
                for (let item in newProps.style) {
                    parentNode["style"][item] = newProps.style[item];
                }
                parentNode.setAttribute("class", `${this._parentClass} ${newProps.className}`);
            }
        }
    }
}
